import React, { Ref, useEffect, useMemo, useRef } from 'react';
import FlatPickr, { FlatPickrProps, FlatPickrRefType } from 'react-flatpickr';
import flatpickr from 'flatpickr';
import * as locales from 'flatpickr/dist/l10n';
import { TextInput } from './TextInput';
import { ClassNameProp, ValidIconName } from '..';
import { CommonInputProps } from './CommonInputProps';
import {
  handleOnKeyDown,
  isEmptyArray,
  isEmptyString,
  removeSecondsFromDate
} from '../../util';
import {
  ArrayIfMultiElseNullable,
  customDateFormatter,
  DefaultDateFormatter,
  getDateFormattingString,
  getDefaultDateFormattingOptions,
  getDefaultTimeFormattingOptions,
  parseDateWithOptions
} from '../../lib';
import { createFocusTrap, FocusTrap } from 'focus-trap';

// Fallback for the storybook
const locale = (window.SenseConstants?.language ??
  'en') as flatpickr.Options.LocaleKey;
// Localize all date pickers
flatpickr.localize(locales.default[locale]);

/**
 * A little helper function to filter empty strings, undefined and null out of a given array.
 *
 * @author agd
 * @param value the value to filter.
 * @return The filtered array. If the given value is not an array, then it will just return the value as is
 */
function filterNullishValuesEmptyStringsIfArray<
  T extends string | number | Date | null | undefined
>(value: Array<T> | T): Array<T> | T {
  return Array.isArray(value)
    ? value.filter(v => v !== null && v !== undefined && v !== '')
    : value;
}

/**
 * Common props for all date/time pickers.
 *
 * @author Bennett Piater
 */
export interface DateTimePickerProps<Variant extends 'single' | 'multi'>
  extends ClassNameProp,
    Pick<
      flatpickr.Options.Options,
      'allowInput' | 'inline' | 'minDate' | 'maxDate' | 'disableMobile'
    >,
    Pick<
      CommonInputProps<string>,
      'label' | 'error' | 'onBlur' | 'required' | 'disabled'
    > {
  /**
   * Callback to fire whenever the selection changes.
   *
   * It receives an array of selected dates (important for multi-/range-selection) and the current input text value.
   *
   * {@link https://flatpickr.js.org/events/#onchange}
   */
  onChange: (selectedDates: ArrayIfMultiElseNullable<Variant, Date>) => void;
  /**
   * The state value of the component. Setting this will result this being intended to be a controlled component.
   * When passing a value, then passing an onChange handler is expected since the onChange handler is supposed to update the input state in that case.
   */
  value: ArrayIfMultiElseNullable<Variant, Date | string | number>;
}

/**
 * A custom hook to create a focus-trap for a flatpickr instance.
 *
 * @param props important picked props from {@link DateTimePickerProps}. If the picker is inline, then no focus-trap is needed.
 * @author agd
 * @return properties that can easily be destructured into a {@link FlatPickr} component's props
 */
function useDatePickerFocusTrap({
  onBlur,
  inline,
  allowInput
}: Pick<DateTimePickerProps<never>, 'onBlur' | 'inline' | 'allowInput'>): Pick<
  FlatPickrProps,
  'onClose' | 'onOpen' | 'ref'
> {
  // necessary refs
  const pickerRef = useRef<FlatPickrRefType | null>(null);
  const focusTrap = useRef<FocusTrap | null>(null);

  // set up the focusTrap on the flatpickr ref
  useEffect(() => {
    if (!focusTrap.current && pickerRef.current) {
      focusTrap.current = createFocusTrap(
        [
          pickerRef.current?.flatpickr?.calendarContainer,
          // find the closest parent container which makes sense as tabbable container, with a final fallback to simply the document.body
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          pickerRef.current?.node?.form ??
            pickerRef.current?.node?.closest('.ehs-modal') ??
            pickerRef.current?.node?.closest('.ehs-panel') ??
            pickerRef.current?.node?.closest('.portlet-boundary') ??
            document.body
        ],
        {
          escapeDeactivates: false,
          returnFocusOnDeactivate: false // needed, since otherwise the <input> will be focused again, causing the picker to be opened again
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerRef.current]);
  // clean-up
  useEffect(function () {
    return function () {
      // deactivate the trap if we had created it
      if (focusTrap.current) {
        focusTrap.current.deactivate();
        focusTrap.current = null;
      }
    };
  }, []); // on mount only

  return {
    ref: pickerRef,
    onClose: () => {
      focusTrap.current?.deactivate();
      onBlur?.();
    },
    onOpen: () => {
      // check if it's not inline, just to be safe
      if (!inline) {
        focusTrap.current?.activate();
      }
      if (!allowInput) {
        const selectedDateElem = pickerRef.current?.flatpickr.selectedDateElem;
        const todayDateElem = pickerRef.current?.flatpickr.todayDateElem;
        if (selectedDateElem && selectedDateElem.isConnected) {
          selectedDateElem.focus();
        } else todayDateElem?.focus();
      }
    }
  };
}

function renderDatePickerInput({
  label,
  required,
  mode,
  error,
  disabled,
  className,
  inputChange
}: {
  className: string | undefined;
  disabled: boolean | undefined;
  error: string | undefined;
  inputChange?: (value: Date | null) => void;
  label: string;
  mode: 'range' | 'date' | 'datetime';
  required: boolean | undefined;
}) {
  return function DatePickerRenderer(
    {
      placeholder,
      ...props
    }: Omit<FlatPickrProps, 'render' | 'options' | 'children'>,
    ref: Ref<HTMLInputElement>
  ): JSX.Element {
    let icon: ValidIconName;
    switch (mode) {
      case 'date':
        icon = 'calendar';
        break;
      case 'datetime':
        icon = 'schedule';
        break;
      case 'range':
        icon = 'calendar-plus';
        break;
    }

    // check if the value is currently empty or not and pass the result to the TextInput
    // This is necessary, because the change listener on the TextInput doesn't actually get fired
    // when the flatpickr is updated, due to the way the flatpickr is implemented.
    let valueEmpty: boolean | undefined = false;
    if (
      (typeof props.value === 'string' && isEmptyString(props.value)) ||
      (Array.isArray(props.value) &&
        (isEmptyArray(props.value) ||
          isEmptyArray(
            props.value.filter(val => val !== undefined && val !== null)
          )))
    ) {
      valueEmpty = true;
    } else if (props.value === undefined || props.value === null) {
      valueEmpty = undefined;
    }

    return (
      <TextInput
        placeholder={placeholder}
        ref={ref}
        type="text"
        label={label}
        rightIcon={icon}
        error={error}
        required={required}
        disabled={disabled}
        valueEmpty={valueEmpty}
        className={className}
        // onBlur={() => inputChange}
      />
    );
  };
}

/**
 * A simple time picker.
 *
 * @param props {@link DateTimePickerProps}
 */
export function TimePicker({
  label,
  value,
  className,
  disabled,
  error,
  onBlur,
  required,
  onChange,
  ...options
}: DateTimePickerProps<'single'>): JSX.Element {
  const propsFromHook = useDatePickerFocusTrap({
    onBlur,
    inline: options.inline
  });
  /* We need to avoid of seconds due to a problem with mobile view in FlatPickr library
  GitHub issue: https://github.com/flatpickr/flatpickr/issues/2246 */
  const dateWithoutSeconds = removeSecondsFromDate(value);
  const dateFormatter = useMemo(() => {
    return customDateFormatter({
      ...getDefaultTimeFormattingOptions(),
      second: undefined
    });
  }, []);

  const placeholder = useMemo(
    () =>
      getDateFormattingString(
        new Intl.DateTimeFormat(dateFormatter.locale, dateFormatter.options)
      ),
    [dateFormatter.locale, dateFormatter.options]
  );
  return (
    <FlatPickr
      {...propsFromHook}
      disabled={disabled}
      value={dateWithoutSeconds}
      options={{
        ...options,
        enableTime: true,
        noCalendar: true,
        allowInvalidPreload: true,
        formatDate: date => dateFormatter.formatDate(date),
        parseDate: input => parseDateWithOptions(input, dateFormatter),
        onKeyDown: (
          _selectedDates,
          _dateStr,
          instance: flatpickr.Instance,
          event
        ) => {
          handleOnKeyDown(instance, event, options);
        },
        allowInput: options.allowInput ?? true
      }}
      placeholder={placeholder}
      readOnly={!(options.allowInput ?? true)}
      render={renderDatePickerInput({
        mode: 'date',
        label,
        required,
        error,
        disabled,
        className
      })}
    />
  );
}

/**
 * Props for the date picker.
 */
export interface DatePickerProps<Variant extends 'single' | 'multi'>
  extends DateTimePickerProps<Variant> {
  /**
   * Whether single or multiple selection is used
   */
  variant: Variant;
}

/**
 * A date picker.
 *
 * It also supports multi-selection.
 *
 * @param props {@link DatePickerProps}
 */
export function DatePicker<Variant extends 'single' | 'multi'>(
  props: DatePickerProps<Variant> & {
    variant: 'single';
    value: Date | null;
    onChange: (selectedDates: Date | null) => void;
  }
): JSX.Element;

export function DatePicker<Variant extends 'single' | 'multi'>(
  props: DatePickerProps<Variant> & {
    variant: 'multi';
    value: Date[];
    onChange: (selectedDates: Date[]) => void;
  }
): JSX.Element;

export function DatePicker<Variant extends 'single' | 'multi'>({
  label,
  value,
  className,
  disabled,
  variant,
  error,
  required,
  onBlur,
  onChange,
  ...options
}: DatePickerProps<Variant>): JSX.Element {
  const propsFromHook = useDatePickerFocusTrap({
    onBlur,
    inline: options.inline,
    allowInput: options.allowInput
  });
  const dateFormatter = useMemo(() => DefaultDateFormatter, []);
  const placeholder = useMemo((): string => {
    const placeHolder = getDateFormattingString(
      new Intl.DateTimeFormat(dateFormatter.locale, dateFormatter.options)
    );
    if (variant === 'multi') {
      return `${placeHolder}, ${placeHolder}, ...`;
    } else {
      return placeHolder;
    }
  }, [dateFormatter.locale, dateFormatter.options, variant]);
  return (
    <FlatPickr
      {...propsFromHook}
      disabled={disabled}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      value={filterNullishValuesEmptyStringsIfArray<typeof value>(value)}
      options={{
        mode: variant === 'multi' ? 'multiple' : 'single',
        ...options,
        allowInvalidPreload: true,
        formatDate: date => dateFormatter.formatDate(date),
        // NOTE: this ignores the formatting options from flatpickr, but it should match our formatDate...
        parseDate: input => parseDateWithOptions(input, dateFormatter),
        onKeyDown: (
          _selectedDates,
          _dateStr,
          instance: flatpickr.Instance,
          event
        ) => {
          handleOnKeyDown(instance, event, options);
        },
        allowInput: options.allowInput ?? true
      }}
      placeholder={placeholder}
      readOnly={!(options.allowInput ?? true)}
      render={renderDatePickerInput({
        mode: 'date',
        label,
        required,
        error,
        disabled,
        className
      })}
      onChange={values => {
        variant === 'multi'
          ? onChange(values as ArrayIfMultiElseNullable<Variant, Date>)
          : onChange(
              (isEmptyArray(values)
                ? null
                : values[0]) as ArrayIfMultiElseNullable<Variant, Date>
            );
      }}
    />
  );
}

/**
 * Correct the times to maximize the range, fulfilling human expectations.
 *
 * I wish Javascript had a type for dates without time component...
 */
function correctRangeTimes(values: Array<Date>): Array<Date> {
  if (isEmptyArray(values)) return values;
  if (values.length === 1) {
    const from = values[0];
    from.setHours(0);
    from.setMinutes(0);
    from.setSeconds(0);
    return [from];
  }
  const from = values[0];
  from.setHours(0);
  from.setMinutes(0);
  from.setSeconds(0);
  const to = values[1];
  to.setHours(23);
  to.setMinutes(59);
  to.setSeconds(59);
  return [from, to];
}

//Setting the icon to a hyphen, so we don't have to translate 'to' in different languages
const rangeSeparator = ' - ';
flatpickr.l10ns.default.rangeSeparator = rangeSeparator;
/**
 * A picker for date ranges.
 *
 * @param params {@link DateTimePickerProps}
 */
export function DateRangePicker({
  label,
  value,
  className,
  disabled,
  error,
  required,
  onBlur,
  onChange,
  ...options
}: DateTimePickerProps<'multi'>): JSX.Element {
  const propsFromHook = useDatePickerFocusTrap({
    onBlur,
    inline: options.inline
  });
  const dateFormatter = useMemo(() => DefaultDateFormatter, []);
  const rangeSeparatorRef = useRef(rangeSeparator);
  const placeholder = useMemo(() => {
    const placeholder = getDateFormattingString(
      new Intl.DateTimeFormat(dateFormatter.locale, dateFormatter.options)
    );
    return `${placeholder}${rangeSeparatorRef.current}${placeholder}`;
  }, [dateFormatter.locale, dateFormatter.options]);
  return (
    <FlatPickr
      {...propsFromHook}
      disabled={disabled}
      value={filterNullishValuesEmptyStringsIfArray(value)}
      placeholder={placeholder}
      options={{
        mode: 'range',
        ...options,
        allowInvalidPreload: true,
        formatDate: date => dateFormatter.formatDate(date),
        parseDate: (input: string) =>
          parseDateWithOptions(input, dateFormatter),
        onKeyDown: (
          _selectedDates,
          _dateStr,
          instance: flatpickr.Instance,
          event
        ) => {
          handleOnKeyDown(instance, event, options);
        },
        allowInput: options.allowInput ?? true
      }}
      onChange={values => onChange(correctRangeTimes(values))}
      readOnly={!(options.allowInput ?? true)}
      render={renderDatePickerInput({
        mode: 'range',
        label,
        required,
        error,
        disabled,
        className
      })}
    />
  );
}

/**
 * A combined date/time picker.
 *
 * @param params {@link DateTimePickerProps}
 */
export function DateTimePicker({
  label,
  value,
  className,
  disabled,
  error,
  required,
  onBlur,
  onChange,
  ...options
}: DateTimePickerProps<'single'>): JSX.Element {
  const propsFromHook = useDatePickerFocusTrap({
    onBlur,
    inline: options.inline
  });
  /* We need to avoid of seconds due to a problem with mobile view in FlatPickr library
        GitHub issue: https://github.com/flatpickr/flatpickr/issues/2246 */
  const dateWithoutSeconds = removeSecondsFromDate(value);
  const dateFormatter = useMemo(() => {
    return customDateFormatter({
      ...getDefaultDateFormattingOptions(),
      ...getDefaultTimeFormattingOptions(),
      second: undefined
    });
  }, []);
  const placeholder = useMemo(
    () =>
      getDateFormattingString(
        new Intl.DateTimeFormat(dateFormatter.locale, dateFormatter.options)
      ),
    [dateFormatter.locale, dateFormatter.options]
  );
  return (
    <FlatPickr
      {...propsFromHook}
      disabled={disabled}
      value={dateWithoutSeconds}
      placeholder={placeholder}
      readOnly={!(options.allowInput ?? true)}
      options={{
        ...options,
        mode: 'single',
        enableTime: true,
        allowInvalidPreload: true,
        formatDate: date => dateFormatter.formatDate(date),
        parseDate: (input: string) =>
          parseDateWithOptions(input, dateFormatter),
        onKeyDown: (
          _selectedDates,
          _dateStr,
          instance: flatpickr.Instance,
          event
        ) => {
          handleOnKeyDown(instance, event, options);
        },
        allowInput: options.allowInput ?? true
      }}
      render={renderDatePickerInput({
        mode: 'datetime',
        label,
        required,
        error,
        disabled,
        className
      })}
      onChange={values => onChange(isEmptyArray(values) ? null : values[0])}
    />
  );
}
